import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import styled from '@emotion/styled';

import { TextRow } from '@innovamat/glimmer-components';
import {
  MaterialsTable,
  TableSubRow,
  TableSubRowCell,
} from '../../../../components';

import { Loader } from '@innovamat/glimmer-components';

import {
  CoursesQuery,
  OrderDetailsQuery,
  useOrderDetailsQuery,
} from '@innovamat/glow-api-client';

import { locales } from '@innovamat/localization';

import { useUser } from '@innovamat/ga-features';
import type {
  HistoryData,
  HistoryRow,
  Material,
} from '../../orders-returns-history.types';

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const LoaderText = styled.span`
  margin-left: 0.5rem;
`;

const SubRowTitle = styled.p`
  margin-bottom: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #333;
`;

const BillingAddressValue = styled(TextRow)`
  font-size: 0.7rem;
  color: #4b5563;
`;

const ShippingAddressValue = styled(TextRow)`
  font-size: 0.7rem;
  color: #4b5563;
`;

type Props = {
  row: HistoryRow;
  setTableData: (data: HistoryData[]) => void;
  tableData: HistoryData[];
  coursesData: CoursesQuery | undefined;
};

const SubRow = ({ row, tableData, setTableData, coursesData }: Props) => {
  const { t } = useTranslation();
  const { user } = useUser();

  const onDataFetchCompleted = (
    responseData: OrderDetailsQuery
  ): void | undefined => {
    if (!responseData?.orderDetails) return;

    const newTableData = tableData.map((currentRow) => {
      if (currentRow.id !== row.original.id) {
        return currentRow;
      }

      const newMaterials: Material[] = responseData.orderDetails!.materials.map(
        (material) => {
          const typedMaterial = material as Material;
          return {
            ...typedMaterial,
            language: locales.getInnovamat3(typedMaterial.language)
              ? locales.getInnovamat3(typedMaterial.language).toUpperCase()
              : '',
            units: t('common.unitsAbbreviation'),
            course:
              coursesData?.courses?.find(
                (currentCourse) =>
                  currentCourse?.order === typedMaterial.courseOrder
              )?.name ?? '-',
          } as Material;
        }
      );

      return {
        ...currentRow,
        subRows: {
          ...responseData.orderDetails,
          materials: newMaterials,
        },
      };
    });

    setTableData(newTableData);
  };

  const {
    data,
    isLoading: isLoadingDetails,
    isSuccess,
  } = useOrderDetailsQuery({
    orderId: row.original.id,
    regionId: user ? user.region : '',
  });

  const { subRows } = row.original;

  const renderShippingAddressTitle = () => {
    return (
      <SubRowTitle>
        {t(`ordersReturnsHistory.${row.original.orderType}ShippingAddress`)}
      </SubRowTitle>
    );
  };

  const renderShippingAddress = () => {
    if (subRows.shippingAddress.length === 0) {
      if (row.original.transactionType === 'prescription') {
        return (
          <SubRowTitle>
            {t(`ordersReturnsHistory.shippingAddress.noAddressPrescription`)}
          </SubRowTitle>
        );
      }

      return (
        <>
          {renderShippingAddressTitle()}
          <ShippingAddressValue isOpaque>
            {t(`ordersReturnsHistory.shippingAddress.noAddress`)}
          </ShippingAddressValue>
        </>
      );
    }

    return (
      <>
        {renderShippingAddressTitle()}
        {subRows.shippingAddress.map((currentRow: string, i: number) => (
          <ShippingAddressValue isOpaque elipsisOnRows={2} key={i}>
            {currentRow}
          </ShippingAddressValue>
        ))}
      </>
    );
  };

  useEffect(() => {
    if (isSuccess && data) {
      onDataFetchCompleted(data);
    }
  }, [isSuccess, data]);

  if (!subRows || isLoadingDetails) {
    return (
      <TableSubRow key={`${row.original.id}_subrow`} isExpanded>
        <TableSubRowCell colSpan={7}>
          <LoaderContainer>
            <Loader />
            <LoaderText>{t('common.loadingContent')}</LoaderText>
          </LoaderContainer>
        </TableSubRowCell>
      </TableSubRow>
    );
  }

  return (
    <TableSubRow key={`${row.original.id}_subrow`} isExpanded>
      <TableSubRowCell>{renderShippingAddress()}</TableSubRowCell>

      <TableSubRowCell colSpan={4}>
        <SubRowTitle>
          {t(`ordersReturnsHistory.${row.original.orderType}Materials`)}
        </SubRowTitle>
        <MaterialsTable materials={subRows.materials as Material[]} />
      </TableSubRowCell>

      <TableSubRowCell colSpan={2}>
        <SubRowTitle>{t('ordersReturnsHistory.billingAddress')}</SubRowTitle>
        {subRows.billingAddress.map((subRow: string, i: number) => (
          <BillingAddressValue isOpaque elipsisOnRows={2} key={i}>
            {subRow}
          </BillingAddressValue>
        ))}
      </TableSubRowCell>
    </TableSubRow>
  );
};

export { SubRow };
